/* eslint-disable jira/naming/disallow-filenames */
/* eslint-disable jira/filename-case */
import React, { type ReactElement } from 'react';
import { envType } from '@atlassiansox/analytics-web-client/analytics-web-types';
import EngagementProviderWithoutConfig from '@atlassiansox/engagekit-ts/controllers/engagement-provider/index';
import { ProductIds } from '@atlassian/ipm-choreographer/constants';
import { getAnalyticsWebClientPromise } from '@jira/platform__observability-product-analytics-web-client-async';
import {
	toEnvironment,
	type Environment,
} from '@jira/platform__tenant-context-types/src/tenant-context.tsx';
import getMeta from '@jira/platform__utils-get-meta/src/utils/get-meta/index.tsx';

type Props = {
	children: ReactElement;
	orgId?: string;
	workspaceId?: string;
};
/**
 * Conditionally renders its children or enriches them with specific configurations for engagement and analytics. This component acts differently based on the execution environment (server vs client) and the availability of user metadata, aiming to seamlessly integrate analytics functionality where applicable.
 */
const EngagementProvider = (props: Props): React.JSX.Element => {
	if (__SERVER__) {
		return props.children;
	}

	const environment = toEnvironment(getMeta('ajs-environment'));
	const getEnvironmentType = (env: Environment | null) => {
		if (env === 'prod') {
			return envType.PROD;
		}
		if (env === 'staging') {
			return envType.STAGING;
		}
		return envType.DEV;
	};

	const aaid = getMeta('ajs-atlassian-account-id');
	if (aaid === null) {
		return props.children;
	}

	const config = {
		aaid,
		cloudId: getMeta('ajs-cloud-id') || '',
		locale: getMeta('ajs-user-locale') || '',
		product: 'jira',
		stargateUrl: '/gateway/api',
		orgId: props.orgId,
	};

	const coordinationOptions = {
		envType: getEnvironmentType(environment),
		enableChoreographer: true,
		additionalData: { sourceProductId: ProductIds.JIRA },
	};

	return (
		<EngagementProviderWithoutConfig
			{...props}
			config={config}
			analyticsClientInstance={Promise.resolve(
				getAnalyticsWebClientPromise().then((client) => client.getInstance()),
			)}
			coordinationOptions={coordinationOptions}
		/>
	);
};
export default EngagementProvider;
